import { useRef, type ReactNode } from 'react';
import {
  type PointsAggregation,
  type PointsDefinition,
  type PointsScope,
  type PointsScoring,
  type PointsTableRow,
  type Tiebreaker,
  TIEBREAKERS,
} from './definition';
import {
  DEFAULT_BUY_IN_BANDS,
  EXPRESSION_PRESETS,
  SHRPO_FIELD_BANDS,
  SHRPO_ROWS,
} from './presets';
import { ExpressionEditor } from './ExpressionEditor';
import { MultiplierEditor } from './MultiplierEditor';
import { PlacementTableEditor } from './PlacementTableEditor';
import {
  PointsPreviewPane,
  type PointsBreakdownAccess,
  type PointsCompare,
  type PointsPreviewState,
} from './PointsPreviewPane';
import { type ScopeEventOption } from './ScopeEditor';
import {
  BuilderSection,
  ChoiceList,
  CheckboxRow,
  IssueList,
  Labelled,
  NumberInput,
  SegmentedControl,
  Worked,
  controlClass,
  inputClass,
  smallButtonClass,
} from './controls';
import { issuesFor, type DefinitionValidation } from './validate';
import {
  BUY_IN_METRIC_CHOICES,
  DEAL_CHOICES,
  FIELD_METRIC_CHOICES,
  PARTICIPATION_CHOICES,
  PARTICIPATION_COUNT_CHOICES,
  bustedBulletsDetail,
  participationMultiplierExample,
  participationValueLabel,
  REENTRY_CHOICES,
  TIEBREAKER_DETAILS,
  TIEBREAKER_LABELS,
} from './wording';

/**
 * §9.1 and §9.2 — the points system designer.
 *
 * <b>Two panes: the definition on the left, the standings it produces on the right.</b> The
 * preview is not a panel bolted on; it is half of what the screen is for. Somebody tuning a
 * system that decides tens of thousands in prizes needs to see who would have won, and see it
 * move as they edit.
 *
 * The sections run in §9.2's order and are numbered, because an operator meeting this for the
 * first time is following a sequence: what it is, what a finish is worth, what weights that, what
 * busting out is worth, what a chop does, and how a season adds up.
 *
 * <b>What is deliberately absent is a Scope section (§9.2).</b> Dates, tags and event filters
 * govern a *leaderboard*, not a points system (§3.2) — that separation is what lets one system
 * serve the 2026 series, the 2027 series and a weekly board without being edited. The builder
 * still has scope controls, but they sit in the preview pane (§9.8) and select which results to
 * score while designing. They are a test harness, not part of the system being built, and the
 * pane says so.
 *
 * <b>This component is presentational.</b> It holds no draft, fetches nothing, and saves nothing:
 * the app above it owns the definition, the test scope, the preview call and the buttons in
 * `header`/`footer`. That is what lets admin-web and room-web run the same builder over two
 * different catalogues, two different sets of permissions and two visual directions.
 */
export function PointsSystemBuilder({
  value,
  onChange,
  validation,
  scope,
  onScopeChange,
  scopeValidation,
  readOnly = false,
  scopeReadOnly = false,
  readOnlyNotice,
  tags,
  events,
  eventsTruncated = false,
  eventsFailed = false,
  preview,
  previewHeader,
  previewCompare,
  previewBreakdown,
  previewClassName = 'lg:sticky lg:top-0 lg:max-h-[100dvh]',
  header,
  footer,
}: {
  value: PointsDefinition;
  onChange: (next: PointsDefinition) => void;
  validation: DefinitionValidation;
  /**
   * §9.8's test scope, which the preview pane owns. Separate from `value` because it is a separate
   * document with a separate owner: it persists to `PointsSystemVersion.PreviewScope` and never
   * reaches the engine.
   */
  scope: PointsScope;
  onScopeChange: (next: PointsScope) => void;
  scopeValidation: DefinitionValidation;
  /**
   * The **definition** is frozen — every control on the left is read-only and the notice says why.
   *
   * Deliberately not the preview pane's business: §9.6 locks `Definition` and nothing else, and
   * warns in as many words that disabling the preview on a published version is the single most
   * likely misreading of the freeze rule. See `scopeReadOnly`.
   */
  readOnly?: boolean;
  /**
   * Whether the preview pane's own controls are read-only as well.
   *
   * A separate answer from `readOnly` and usually a different one, because the two ask different
   * questions. `readOnly` asks whether this *document* may change; this asks whether *this caller*
   * can persist a test scope at all — which is an ownership question (§2.2: a room may not write
   * to a platform system), not a lifecycle one. Defaults to false, since a frozen version is the
   * case this exists to keep interactive.
   */
  scopeReadOnly?: boolean;
  readOnlyNotice?: ReactNode;
  tags: readonly string[];
  /** The room's events, or null while they are being read or when the read failed (known gap 112). */
  events: readonly ScopeEventOption[] | null;
  eventsTruncated?: boolean;
  /** Whether a null `events` is a read that failed rather than one still on its way. */
  eventsFailed?: boolean;
  preview: PointsPreviewState;
  previewHeader?: ReactNode;
  /** §9.8's compare affordance — the draft against the previously published version. */
  previewCompare?: PointsCompare;
  /** §7.4's per-player working, opened from a standings row. Omitted leaves rows unexpandable. */
  previewBreakdown?: PointsBreakdownAccess;
  /** Sizing for the sticky pane, since the two apps' shells scroll differently. */
  previewClassName?: string;
  header?: ReactNode;
  footer?: ReactNode;
}) {
  const set = <K extends keyof PointsDefinition>(key: K, next: PointsDefinition[K]) =>
    onChange({ ...value, [key]: next });

  /**
   * The other branch of the scoring switch, kept for as long as the screen is open.
   *
   * `PointsScoring` is a discriminated union, so a definition holds a table *or* a formula and
   * never both — which means switching the segmented control would otherwise throw away whatever
   * was in the other one. Somebody trying an expression against a table they spent ten minutes on
   * would find it gone when they switched back, and the placement editor's own "choosing a preset
   * replaces every row" warning would have been the only notice they ever got.
   *
   * This is deliberately a ref rather than draft state: it is not part of the definition, it does
   * not make the screen dirty, and it is gone when the builder closes. Nothing is lost that was
   * ever saved — the document on the server is whichever branch was current at the last save.
   */
  const remembered = useRef<{ rows: PointsTableRow[]; expression: string }>({
    rows: SHRPO_ROWS.map((row) => ({ ...row })),
    expression: EXPRESSION_PRESETS[0]!.expression,
  });

  if (value.scoring.type === 'table') remembered.current.rows = value.scoring.rows;
  else remembered.current.expression = value.scoring.expression;

  const setScoringType = (type: PointsScoring['type']) => {
    if (type === value.scoring.type) return;

    set(
      'scoring',
      type === 'table'
        ? { type: 'table', rows: remembered.current.rows }
        : { type: 'formula', expression: remembered.current.expression },
    );
  };

  return (
    <div>
      {header}
      {readOnly && readOnlyNotice ? (
        <div className="mb-4 border border-line border-l-4 border-l-warn bg-surface p-3 text-[12.5px] text-ink-900">
          {readOnlyNotice}
        </div>
      ) : null}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6 border border-line bg-surface p-5">
          <BuilderSection step={1} title="What this system is">
            <div className="space-y-3">
              <Labelled label="Name">
                <input
                  type="text"
                  value={value.name}
                  disabled={readOnly}
                  maxLength={200}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Summer Series Player of the Year"
                  className={inputClass}
                />
              </Labelled>
              <Labelled
                label="Description"
                hint="Optional. What it does in a line — this is what somebody reads a year from now."
              >
                <input
                  type="text"
                  value={value.description ?? ''}
                  disabled={readOnly}
                  maxLength={1000}
                  onChange={(e) => set('description', e.target.value === '' ? null : e.target.value)}
                  placeholder="Placement table × field size, everyone in a chop scores the lowest place"
                  className={inputClass}
                />
              </Labelled>
              <IssueList issues={issuesFor(validation, 'identity')} />
            </div>
          </BuilderSection>

          <BuilderSection
            step={2}
            title="What a finish is worth"
            summary={
              value.scoring.type === 'table'
                ? undefined
                : 'A formula is the advanced path. Most rooms want a placement table — it is readable at a glance, and a formula is not.'
            }
            action={
              <SegmentedControl
                ariaLabel="How finishes are scored"
                value={value.scoring.type}
                onChange={setScoringType}
                options={[
                  { value: 'table', label: 'Placement table', disabled: readOnly },
                  { value: 'formula', label: 'Expression', disabled: readOnly },
                ]}
              />
            }
          >
            {value.scoring.type === 'table' ? (
              <PlacementTableEditor
                rows={value.scoring.rows}
                onChange={(rows) => set('scoring', { type: 'table', rows })}
                validation={validation}
                readOnly={readOnly}
              />
            ) : (
              <ExpressionEditor
                value={value.scoring.expression}
                onChange={(expression) => set('scoring', { type: 'formula', expression })}
                validation={validation}
                readOnly={readOnly}
              />
            )}
          </BuilderSection>

          <BuilderSection
            step={3}
            title="What counts for more"
            summary="Optional. Off means no weighting at all — not a ladder that happens to be 1.0 everywhere."
          >
            <div className="space-y-5">
              <MultiplierEditor
                section="fieldMultiplier"
                value={value.fieldMultiplier}
                onChange={(next) => set('fieldMultiplier', next)}
                metrics={FIELD_METRIC_CHOICES}
                defaults={() => ({
                  ...SHRPO_FIELD_BANDS,
                  bands: SHRPO_FIELD_BANDS.bands.map((band) => ({ ...band })),
                })}
                subject="field size"
                switchLabel="Bigger fields are worth more"
                switchDetail="A win in a 900-runner event outscores a win in a 40-runner one."
                validation={validation}
                readOnly={readOnly}
              />
              <MultiplierEditor
                section="buyInMultiplier"
                value={value.buyInMultiplier}
                onChange={(next) => set('buyInMultiplier', next)}
                metrics={BUY_IN_METRIC_CHOICES}
                defaults={() => ({
                  ...DEFAULT_BUY_IN_BANDS,
                  bands: DEFAULT_BUY_IN_BANDS.bands.map((band) => ({ ...band })),
                })}
                subject="buy-in"
                switchLabel="Bigger buy-ins are worth more"
                switchDetail="Leave this off to score a $300 event and a $25,500 High Roller the same, which is what SHRPO does."
                validation={validation}
                readOnly={readOnly}
              />
            </div>
          </BuilderSection>

          <BuilderSection step={4} title="Points for players who bust before the money">
            <div className="space-y-4">
              <ChoiceList
                name="participation-mode"
                options={PARTICIPATION_CHOICES}
                value={value.participation.mode}
                disabled={readOnly}
                onChange={(mode) => set('participation', { ...value.participation, mode })}
              />

              {value.participation.mode !== 'none' ? (
                <div className="space-y-3 border-l-2 border-line pl-4">
                  <ChoiceList
                    name="participation-count"
                    options={PARTICIPATION_COUNT_CHOICES}
                    value={value.participation.countEntries}
                    disabled={readOnly}
                    onChange={(countEntries) =>
                      set('participation', { ...value.participation, countEntries })
                    }
                  />

                  <Labelled
                    label={participationValueLabel(value.participation.countEntries)}
                    className="max-w-[10rem]"
                  >
                    <NumberInput
                      value={value.participation.pointsPerEntry}
                      onChange={(pointsPerEntry) =>
                        set('participation', { ...value.participation, pointsPerEntry })
                      }
                      ariaLabel={participationValueLabel(value.participation.countEntries)}
                      min={0}
                      step={0.5}
                      disabled={readOnly}
                      className="w-full"
                    />
                  </Labelled>

                  <CheckboxRow
                    checked={value.participation.applyFieldMultiplier}
                    disabled={readOnly}
                    onChange={(applyFieldMultiplier) =>
                      set('participation', { ...value.participation, applyFieldMultiplier })
                    }
                    label="Weight these by field size, the same way as a finish"
                    detail={participationMultiplierExample(value, 'field')}
                  />

                  <CheckboxRow
                    checked={value.participation.applyBuyInMultiplier}
                    disabled={readOnly}
                    onChange={(applyBuyInMultiplier) =>
                      set('participation', { ...value.participation, applyBuyInMultiplier })
                    }
                    label="Weight these by buy-in, the same way as a finish"
                    detail={participationMultiplierExample(value, 'buyIn')}
                  />

                  <CheckboxRow
                    checked={value.participation.countBustedBulletsWhenCashed}
                    disabled={readOnly}
                    onChange={(countBustedBulletsWhenCashed) =>
                      set('participation', {
                        ...value.participation,
                        countBustedBulletsWhenCashed,
                      })
                    }
                    label="Busted re-entries still count when the player goes on to cash"
                    detail={bustedBulletsDetail(value.participation.countEntries)}
                  />

                  <IssueList issues={issuesFor(validation, 'participation')} />
                </div>
              ) : null}

              <div className="border-t border-line pt-4">
                <div className="mb-2 text-xs text-ink-800/60">
                  When a player enters the same event more than once
                </div>
                <ChoiceList
                  name="reentry-policy"
                  options={REENTRY_CHOICES}
                  value={value.reentryPolicy}
                  disabled={readOnly}
                  onChange={(reentryPolicy) => set('reentryPolicy', reentryPolicy)}
                />
              </div>
            </div>
          </BuilderSection>

          <BuilderSection
            step={5}
            title="When players chop"
            summary="A negotiated finish doesn’t say who beat whom, so every serious system adjusts for it."
          >
            <ChoiceList
              name="deal-handling"
              options={DEAL_CHOICES}
              value={value.dealHandling}
              disabled={readOnly}
              onChange={(dealHandling) => set('dealHandling', dealHandling)}
            />
          </BuilderSection>

          <BuilderSection step={6} title="How a season adds up">
            <div className="space-y-4">
              <AggregationEditor
                value={value.aggregation}
                onChange={(aggregation) => set('aggregation', aggregation)}
                readOnly={readOnly}
              />
              <IssueList issues={issuesFor(validation, 'aggregation')} />

              <TiebreakerEditor
                value={value.tiebreakers}
                onChange={(tiebreakers) => set('tiebreakers', tiebreakers)}
                readOnly={readOnly}
              />

              <Labelled
                label="Decimal places shown"
                hint="Display only. Ranking always uses the full figure, so two players shown as 90.12 can still be ordered."
                className="max-w-[10rem]"
              >
                <NumberInput
                  value={value.display.decimalPlaces}
                  onChange={(n) => set('display', { decimalPlaces: n ?? 0 })}
                  ariaLabel="Decimal places shown"
                  min={0}
                  step={1}
                  disabled={readOnly}
                  className="w-full"
                />
              </Labelled>
              <IssueList issues={issuesFor(validation, 'display')} />
            </div>
          </BuilderSection>
        </div>

        <aside
          className={`flex min-w-0 flex-col overflow-hidden border border-line bg-surface ${previewClassName}`}
        >
          <PointsPreviewPane
            state={preview}
            scope={scope}
            onScopeChange={onScopeChange}
            scopeValidation={scopeValidation}
            tags={tags}
            events={events}
            eventsTruncated={eventsTruncated}
            eventsFailed={eventsFailed}
            // Not `readOnly`. §9.6: the definition side goes read-only when a version publishes
            // and the preview pane does not.
            readOnly={scopeReadOnly}
            decimalPlaces={
              Number.isInteger(value.display.decimalPlaces) &&
              value.display.decimalPlaces >= 0 &&
              value.display.decimalPlaces <= 6
                ? value.display.decimalPlaces
                : 2
            }
            compare={previewCompare}
            breakdown={previewBreakdown}
            header={previewHeader}
          />
        </aside>
      </div>

      {footer}
    </div>
  );
}

function AggregationEditor({
  value,
  onChange,
  readOnly,
}: {
  value: PointsAggregation;
  onChange: (next: PointsAggregation) => void;
  readOnly: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs text-ink-800/60">A player’s total is</div>
      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="radio"
          name="aggregation"
          checked={value.mode === 'sumAll'}
          disabled={readOnly}
          onChange={() => onChange({ mode: 'sumAll' })}
          className="mt-0.5 accent-[var(--color-felt-700)]"
        />
        <span>
          <span className="block text-[13px] text-ink-950">every event they played, added up</span>
          <span className="mt-0.5 block text-[11.5px] text-ink-800/60">
            Volume counts. Playing more can only help.
          </span>
        </span>
      </label>

      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="radio"
          name="aggregation"
          checked={value.mode === 'bestN'}
          disabled={readOnly}
          onChange={() => onChange({ mode: 'bestN', n: 5 })}
          className="mt-0.5 accent-[var(--color-felt-700)]"
        />
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2 text-[13px] text-ink-950">
            their best
            <NumberInput
              value={value.mode === 'bestN' ? value.n : 5}
              onChange={(n) => onChange({ mode: 'bestN', n: n ?? 1 })}
              ariaLabel="How many events count"
              min={1}
              step={1}
              disabled={readOnly || value.mode !== 'bestN'}
              className="w-16"
            />
            events only
          </span>
          <span className="mt-0.5 block text-[11.5px] text-ink-800/60">
            A player with fewer than that has all of them counted, so nobody is penalised for the
            series being short.
          </span>
        </span>
      </label>
    </div>
  );
}

/**
 * The tiebreak chain, in order.
 *
 * Order is the whole content here — the list is applied top to bottom until the tie resolves —
 * so it is a reorderable list rather than a set of checkboxes. Players still level after the
 * whole chain share a rank and the next one skips.
 */
function TiebreakerEditor({
  value,
  onChange,
  readOnly,
}: {
  value: Tiebreaker[];
  onChange: (next: Tiebreaker[]) => void;
  readOnly: boolean;
}) {
  const unused = TIEBREAKERS.filter((t) => !value.includes(t));

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    [next[index], next[to]] = [next[to]!, next[index]!];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="text-xs text-ink-800/60">When two players are level, break the tie by</div>

      {value.length === 0 ? (
        <p className="text-[11.5px] text-ink-800/55">
          Nothing set — level players will simply share a rank, and the next place skips.
        </p>
      ) : (
        <ol className="space-y-1">
          {value.map((tiebreaker, index) => (
            <li
              key={tiebreaker}
              className="flex items-center gap-2 border border-line bg-surface px-2.5 py-1.5"
            >
              <span className="num w-4 shrink-0 text-[11px] text-ink-800/40">{index + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] text-ink-950">
                  {TIEBREAKER_LABELS[tiebreaker]}
                </span>
                <span className="block text-[10.5px] text-ink-800/50">
                  {TIEBREAKER_DETAILS[tiebreaker]}
                </span>
              </span>
              {!readOnly ? (
                <span className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${TIEBREAKER_LABELS[tiebreaker]} up`}
                    className="px-1 text-xs text-ink-800/40 transition hover:text-ink-950 disabled:opacity-25"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === value.length - 1}
                    aria-label={`Move ${TIEBREAKER_LABELS[tiebreaker]} down`}
                    className="px-1 text-xs text-ink-800/40 transition hover:text-ink-950 disabled:opacity-25"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange(value.filter((t) => t !== tiebreaker))}
                    aria-label={`Remove ${TIEBREAKER_LABELS[tiebreaker]}`}
                    className="px-1 text-sm text-ink-800/40 transition hover:text-bad"
                  >
                    ×
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {!readOnly && unused.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value=""
            aria-label="Add a tiebreak"
            onChange={(e) => {
              if (e.target.value) onChange([...value, e.target.value as Tiebreaker]);
            }}
            className={`${controlClass} max-w-[16rem]`}
          >
            <option value="">Add a tiebreak…</option>
            {unused.map((tiebreaker) => (
              <option key={tiebreaker} value={tiebreaker}>
                {TIEBREAKER_LABELS[tiebreaker]}
              </option>
            ))}
          </select>
          {!value.includes('alphabeticalByLastName') ? (
            <button
              type="button"
              onClick={() => onChange([...value, 'alphabeticalByLastName'])}
              className={smallButtonClass}
            >
              End with last name A–Z
            </button>
          ) : null}
        </div>
      ) : null}

      {value.length > 0 && !value.includes('alphabeticalByLastName') ? (
        <Worked>
          Nothing on this list always settles it, so two players can still finish level and share a
          rank. Last name A–Z at the bottom guarantees an order.
        </Worked>
      ) : null}
    </div>
  );
}
